import { useEffect, useState } from 'react';
import { Alert, Button, Empty, Spin, Tag, Typography } from 'antd';
import { ArrowRightOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';

import { formAPI } from '@/api';
import { AuditLogEntryDTO } from '@/interfaces';
import { UI_DATE_FORMAT } from '@Constants/ui';
import { getFieldLabel } from './field-label-lookup';
import { BORDER_COLOR } from '@Constants/colors';

const { Text } = Typography;

// NOTE (2026-09-09, audit trail - stage 2 of 3, revised): read-only view
// of the AuditLog entries written by the backend (see AuditLogService).
// This component is entirely separate from the CIOMS/Pregnancy/SAE_AESI/
// Adverse Event form components - it does not render, read, or modify any
// existing form field, and has no effect on saving/exporting a form. It is
// rendered by FormContainer directly below the form (not in a side
// drawer), toggled by a "Show History" switch in the header, and lists
// EVERY individual field change (across every save) as its own row,
// newest first - similar in spirit to how Viedoc's inline field history
// trail reads (value change, then who/when underneath).
interface ChangeHistorySectionProps {
  formId: string;
  visible: boolean;
}

interface FlatChangeRow {
  key: string;
  field: string;
  fieldLabel: string;
  oldValue: unknown;
  newValue: unknown;
  action: AuditLogEntryDTO['action'];
  user: AuditLogEntryDTO['user'];
  createdAt: string;
}

const Section = styled.div`
  border-top: 2px solid ${BORDER_COLOR};
  margin-top: 16px;
  padding-top: 16px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const HistoryRow = styled.div`
  border-bottom: 1px solid ${BORDER_COLOR};
  padding: 8px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const flattenEntries = (entries: AuditLogEntryDTO[]): FlatChangeRow[] => {
  const rows: FlatChangeRow[] = [];
  entries.forEach((entry) => {
    entry.changes.forEach((change, index) => {
      rows.push({
        key: `${entry._id}-${index}`,
        field: change.field,
        fieldLabel: getFieldLabel(entry.formType, change.field),
        oldValue: change.oldValue,
        newValue: change.newValue,
        action: entry.action,
        user: entry.user,
        createdAt: entry.createdAt,
      });
    });
  });
  return rows;
};

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '(empty)';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatUser = (user: AuditLogEntryDTO['user']): string => {
  if (!user) {
    return 'System';
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${user.email})` : user.email || 'Unknown user';
};

const formatActionLabel = (action: AuditLogEntryDTO['action']): string =>
  action === 'CREATE' ? 'Initial data entry' : 'Data updated';

const ChangeHistorySection = ({ formId, visible }: ChangeHistorySectionProps) => {
  const [rows, setRows] = useState<FlatChangeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !formId) {
      return;
    }
    setLoading(true);
    setError(null);
    formAPI
      .getFormAuditLog(formId)
      // NOTE (2026-09-09, bug fix): the backend wraps every response as
      // `{ statusCode, data }` (see api/form.ts's getFormAuditLog), so the
      // actual entries array is response.data.data, not response.data.
      .then((response) => setRows(flattenEntries(response.data?.data || [])))
      .catch(() =>
        setError(
          'Could not load change history. Please try again, and if this keeps happening, contact support.'
        )
      )
      .finally(() => setLoading(false));
  }, [visible, formId]);

  if (!visible) {
    return null;
  }

  const renderRow = (row: FlatChangeRow) => (
    <HistoryRow key={row.key}>
      <div>
        <Tag>{row.fieldLabel}</Tag>
        <Tag color="magenta">{formatValue(row.oldValue)}</Tag>
        <ArrowRightOutlined style={{ margin: '0 4px' }} />
        <Tag color="green">{formatValue(row.newValue)}</Tag>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {formatActionLabel(row.action)} | {formatUser(row.user)} |{' '}
        {moment(row.createdAt).format(`${UI_DATE_FORMAT} HH:mm`)}
      </Text>
    </HistoryRow>
  );

  return (
    <Section>
      <SectionTitleRow>
        <SectionTitle>Change History</SectionTitle>
        {/* NOTE (2026-09-09, audit trail - stage 3 of 3): separate CSV
            export, independent from the existing form CSV exports. Shown
            to everyone (not role-gated on the frontend) to match how the
            existing bulk "Download" button on the study page works today
            - access is enforced by the backend's AboveMonitorRoleGuard,
            which returns an error a user without permission would see the
            same way they already do for the existing export. */}
        {rows.length > 0 && (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => formAPI.downloadFormAuditLog(formId)}
          >
            Download CSV
          </Button>
        )}
      </SectionTitleRow>
      {loading ? (
        <Spin />
      ) : error ? (
        <Alert type="error" message="Failed to load change history" description={error} showIcon />
      ) : rows.length === 0 ? (
        <Empty description="No changes recorded yet" />
      ) : (
        rows.map(renderRow)
      )}
    </Section>
  );
};

export default ChangeHistorySection;
